import { create } from 'zustand'
import type { Catalog, CatalogCard } from '@/types/card'
import { loadCatalog, loadSearchData } from '@/services/catalog'
import { getOfflineStates, requestPersistence, type CardOfflineState } from '@/features/offline/storage'
import {
  anzahlUpdates,
  diffCatalog,
  ermittleUpdates,
  markSeen,
  readSeen,
  KEINE_UPDATES,
  type CatalogUpdates,
} from '@/features/updates/diff'
import { setzeBadge } from '@/features/updates/badge'

export type Theme = 'auto' | 'hell' | 'nacht' | 'rot'

/** Muss vor der Store-Erstellung stehen: readStoredTheme() läuft beim Auswerten
 *  des Moduls und käme sonst an eine noch nicht initialisierte Konstante. */
const THEMES = ['auto', 'hell', 'nacht', 'rot'] as const

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

export const THEME_LABELS: Record<Theme, string> = {
  auto: 'Automatisch',
  hell: 'Hell',
  nacht: 'Nacht',
  rot: 'Rotlicht',
}

const THEME_KEY = 'taschenkarten:theme'

/**
 * Anwendungszustand.
 *
 * Bewusst flach gehalten: Katalog, Suchdaten und Offline-Zustand werden einmal geladen
 * und von allen Ansichten geteilt. Die Karten selbst liegen nicht hier, sondern in
 * IndexedDB — hier stehen nur die Metadaten.
 */
interface AppState {
  status: 'lädt' | 'bereit' | 'fehler'
  error: string | null

  catalog: Catalog | null
  /** Woher der Katalog stammt; bei 'lokal' weist die Oberfläche auf den Stand hin. */
  source: 'netz' | 'lokal' | null

  searchData: { index: string; fullTexts: Record<string, string> } | null

  /** Offline-Zustand je Karte. Wird nach jedem Download aufgefrischt. */
  offlineStates: Map<string, CardOfflineState>

  /** Was sich seit dem letzten Besuch geändert hat — leer, solange nichts ansteht. */
  updates: CatalogUpdates

  theme: Theme

  init: () => Promise<void>
  setTheme: (theme: Theme) => void
  refreshOfflineStates: () => Promise<void>
  /** Lädt Katalog und Suchdaten erneut — wenn eine Push-Nachricht neue Karten meldet. */
  refreshCatalog: () => Promise<void>
  /** Liest den gesehenen Stand neu ein — nach dem Öffnen einer gekennzeichneten Karte. */
  refreshUpdates: () => Promise<void>
  /** Der Nutzer hat alle Änderungen zur Kenntnis genommen: Hinweis und Zähler gehen weg. */
  markUpdatesSeen: () => Promise<void>
}

export const useApp = create<AppState>((set, get) => ({
  status: 'lädt',
  error: null,
  catalog: null,
  source: null,
  searchData: null,
  offlineStates: new Map(),
  updates: KEINE_UPDATES,
  theme: readStoredTheme(),

  async init() {
    try {
      const { catalog, source } = await loadCatalog()
      set({ catalog, source })

      // Der Katalog allein macht die App schon bedienbar. Suchdaten und Offline-Zustand
      // dürfen einen Moment später eintreffen.
      set({ status: 'bereit' })

      const [searchData, updates] = await Promise.all([
        loadSearchData(),
        ermittleUpdates(catalog.cards),
        get().refreshOfflineStates(),
      ])
      set({ searchData, updates })
      setzeBadge(anzahlUpdates(updates))

      void requestPersistence()
    } catch (error) {
      set({ status: 'fehler', error: (error as Error).message })
    }
  },

  setTheme(theme) {
    set({ theme })
    applyTheme(theme)
    try {
      localStorage.setItem(THEME_KEY, theme)
    } catch {
      // Privater Modus ohne Speicherzugriff — das Erscheinungsbild gilt dann nur für diese Sitzung.
    }
  },

  /**
   * Erneutes Laden bei laufender App.
   *
   * Bewusst nicht über `init()`: Dort führt jeder Fehlschlag zum Startbildschirm
   * „Start nicht möglich". Hier wäre das falsch — die App läuft ja bereits. Geht
   * etwas schief, bleibt einfach der bisherige Stand stehen.
   *
   * Wird bei jeder Rückkehr aus dem Hintergrund aufgerufen und muss deshalb sparsam
   * sein: Der Katalog wiegt ein Fünftel dessen, was Suchindex und Volltexte zusammen
   * wiegen, und nur er beantwortet die Frage, ob sich überhaupt etwas geändert hat.
   */
  async refreshCatalog() {
    try {
      const bisher = get().catalog?.catalogHash
      const { catalog, source } = await loadCatalog()
      set({ catalog, source })

      // Suchindex und Volltexte hängen an denselben Karten wie der Katalog-Hash. Ist
      // er gleich geblieben, wäre ihr erneutes Laden im Mobilfunknetz bezahlte Zeit
      // für ein unverändertes Ergebnis.
      const searchData =
        catalog.catalogHash === bisher ? null : await loadSearchData()
      if (searchData) set({ searchData })

      await get().refreshOfflineStates()
      await get().refreshUpdates()
    } catch {
      // Kein Netz oder kein brauchbarer Katalog. Der Hinweis kommt beim nächsten Start.
    }
  },

  async refreshUpdates() {
    const catalog = get().catalog
    if (!catalog) return
    const updates = diffCatalog(await readSeen(), catalog.cards)
    set({ updates })
    setzeBadge(anzahlUpdates(updates))
  },

  async markUpdatesSeen() {
    const catalog = get().catalog
    if (!catalog) return
    await markSeen(catalog.cards)
    await get().refreshUpdates()
  },

  async refreshOfflineStates() {
    const catalog = get().catalog
    if (!catalog) return
    set({ offlineStates: await getOfflineStates(catalog.cards) })
  },
}))

/** Karte anhand ihrer ID; `null`, solange der Katalog nicht geladen ist. */
export function useCard(id: string | undefined): CatalogCard | null {
  const catalog = useApp((state) => state.catalog)
  if (!catalog || !id) return null
  return catalog.cards.find((card) => card.id === id) ?? null
}

/**
 * Ist diese Karte neu oder überarbeitet? `null`, wenn sie unverändert ist.
 * Speist die Marke an Listeneinträgen und Favoritenkacheln.
 */
export function useCardUpdate(id: string): 'neu' | 'aktualisiert' | null {
  const updates = useApp((state) => state.updates)
  if (updates.neu.some((card) => card.id === id)) return 'neu'
  if (updates.aktualisiert.some((card) => card.id === id)) return 'aktualisiert'
  return null
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'auto') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)

  // Die Systemleisten des Geräts mitfärben, damit die App nicht wie ein Browserfenster wirkt.
  const color = getComputedStyle(root).getPropertyValue('--bg-deep').trim()
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color || '#0b0f16')
}

function readStoredTheme(): Theme {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(THEME_KEY)
  } catch {
    // Privater Modus ohne Speicherzugriff. Der catch umfasst bewusst nur den Zugriff
    // selbst — ein weiter gefasster Block verschluckt echte Fehler und lässt die
    // Einstellung stillschweigend auf den Standard zurückfallen.
  }
  if (isTheme(stored)) return stored
  // Standard folgt dem Gerät: Wer sein Telefon dunkel gestellt hat, will das auch hier.
  return 'auto'
}
